/**
 * Metadata structure docs - src/flexible-reports/store/docs/Metadata.md
 */

export const DATA_RETENTION_PERIOD = ['YEARS_6', 'MONTHS_15', 'DAYS_90', 'DAYS_30', 'DAYS_14'];
export type DataRetentionPeriod = (typeof DATA_RETENTION_PERIOD)[number];

// Reference: https://code.amazon.com/packages/AdsMetricDetailServiceThickJavaClient/blobs/mainline/--/src/main/java/com/amazon/mds/client/thick/model/DataRetentionPeriod.java
export const RETENTION_DAYS: Record<DataRetentionPeriod, number> = {
  YEARS_6: 2190,
  MONTHS_15: 450,
  DAYS_90: 90,
  DAYS_30: 30,
  DAYS_14: 14
};

export type Classification = 'CONVERTED' | 'EXPANDED' | 'CROSS_ACCOUNT_SUPPORTED';

export type ReportingFieldDataType =
  | 'COUNT'
  | 'HIGH_PRECISION_COUNT'
  | 'CURRENCY_VALUE'
  | 'PRECISION_CURRENCY_VALUE'
  | 'COST_BASED_RATE'
  | 'UNITLESS_RATIO'
  | 'PERCENTAGE_RATE'
  | 'STRING'
  | 'ENUM'
  | 'DATE'
  | 'DATE_RANGE'
  | 'CANONICAL_ID'
  | 'LONG'
  | 'DATE_TIME';

export type EnumValue = {
  name: string;
  value: string;
};

/**
 * id - contains id of the attribute. examples: dimension - 'campaign.id', metric - 'metric.views'
 * name - translated name of the column attribute. examples: dimension - 'Campaign Id', metric - 'Views'
 * displayOrder - sort order of attribute for display order
 * tooltip - translated tooltip content of attribute
 * isDeprecated - indicates the field has advisoryType=DEPRECATION from MDS fieldAdvisory, used to hide it from the column picker
 */
export interface ColumnAttribute {
  id: string;
  name: string;
  displayOrder: number;
  tooltip?: string;
  isFilterable: boolean;
  enumValues: EnumValue[] | null | undefined;
  dataRetentionPeriod: DataRetentionPeriod | null | undefined;
  dataType: ReportingFieldDataType | null | undefined;
  classifications?: Classification[] | null;
  isDeprecated?: boolean;
}

/**
 * id - contains id of the family. examples: dimension - 'Campaign', metric - 'Views'.
 * name - translated name of the column family. examples: dimension - 'Campaign Id', metric - 'Views'
 * attributes - list of attributes for a family. Single attribute is represented as family with null attributes.
 * displayOrder - sort order of family for display order.
 * tooltip - translated tooltip content of family.
 * dataRetentionPeriod - for flattened families, contains the data retention period from the single attribute.
 * isFilterable - for flattened families, indicates if the dimension is filterable.
 * dataType - for flattened families, contains the data type from the single attribute.
 * enumValues - for flattened families, contains the enum values from the single attribute.
 * isDeprecated - for flattened families, propagated from the single attribute's MDS fieldAdvisory deprecation flag.
 */
export interface ColumnFamily {
  id: string;
  name: string;
  category: string;
  attributes: ColumnAttribute[] | null;
  displayOrder: number;
  tooltip?: string;
  classifications?: Classification[] | null;
  dataRetentionPeriod?: DataRetentionPeriod | null;
  isFilterable?: boolean;
  dataType?: ReportingFieldDataType | null;
  enumValues?: EnumValue[] | null;
  isDeprecated?: boolean;
}

/**
 * id - contains id of the category. examples: dimension - 'geography', metric - 'supply'
 * name - translated name of the category. examples: dimension - 'Geography', metric - 'Supply'
 * displayOrder - sort order of category.
 */
export interface Category {
  id: string;
  name: string;
  displayOrder: number;
}

/**
 * id - id of selected family
 * attributes - list of attributes of a selected family.
 */
export interface SelectedFamily {
  id: string;
  attributes: string[];
}

export interface DisabledField {
  id: string;
  disabledBy: string[];
}

export interface InCompatibleField {
  id: string;
  incompatibleWith: string[];
}

export interface RequiredField {
  id: string;
  fields: string[];
}

export interface FieldMetadata {
  classifications?: Classification[] | null;
  complimentaryFields: string[];
}

export type ComplimentaryFieldMap = Record<string, FieldMetadata>;

export const DATE_RANGE_DEFAULT_DIMENSION_ID = 'dateRange.value';
